/*
 * The people index holds more than individuals: nations and people groups
 * (Israelites, Jebusites, Levites), places and polities (Egypt, Nineveh,
 * Kingdom of Judah), and one battle taxonomy entry. They live there because
 * relationships point at them, but they used to render inline among a person's
 * relatives -- Simeon's sibling list included "Israelites".
 *
 * Records carry an explicit `recordType` only when they are NOT an individual,
 * so an absent field means "person".
 *
 * Deliberately not called `type`: nationsOfTheBible.json already uses that key
 * for a human-readable label ("Empire", "Lineage Group"), 34 ids appear in both
 * files, and battle pages render whichever they find -- which surfaced the raw
 * enum as "group" on screen.
 */

export const PERSON = "person";
export const GROUP = "group";
export const PLACE = "place";
export const CATEGORY = "category";

export function getRecordType(record) {
  return record?.recordType || PERSON;
}

export function isIndividual(record) {
  return getRecordType(record) === PERSON;
}

/*
 * Nations, people groups, places and polities: real study destinations, but
 * they belong in their own section rather than in a list of relatives.
 */
export function isGroupOrPlace(record) {
  const type = getRecordType(record);

  return type === GROUP || type === PLACE;
}

/*
 * Taxonomy rows that are not study subjects at all and should never surface
 * as a person (the "Internal" conflict category, aliased "Civil").
 */
export function isNonSubject(record) {
  return getRecordType(record) === CATEGORY;
}

/*
 * Splits a mixed list into individuals and group/place records, dropping
 * anything that is not a study subject.
 */
export function partitionByType(records = []) {
  const individuals = [];
  const groups = [];

  records.forEach((record) => {
    if (!record || isNonSubject(record)) return;

    if (isGroupOrPlace(record)) {
      groups.push(record);
    } else {
      individuals.push(record);
    }
  });

  return { individuals, groups };
}
